import { Check, CircleNotch, Copy, Link as LinkIcon, Trash } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getApiErrorMessage,
  useAdminWifiLink,
  useCreateWifiLink,
  useRevokeWifiLink,
} from "@/lib/api";

const STATUS_LABEL: Record<string, string> = {
  pending: "Waiting for the household",
  submitted: "Used, details received",
  expired: "Expired",
  revoked: "Revoked",
  locked: "Locked after too many failed attempts",
};

interface Props {
  householdId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create, copy and revoke a household's Wi-Fi sharing link.
 *
 * The URL is only ever available in the create response, because the database
 * stores just its hash. That is why this dialog holds it in state and says so
 * plainly: once it closes, the only way back is a new link.
 */
export function WifiLinkDialog({ householdId, open, onOpenChange }: Props) {
  const { data: status, isLoading } = useAdminWifiLink(open ? householdId : null);
  const createLink = useCreateWifiLink();
  const revokeLink = useRevokeWifiLink();

  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    setError("");
    try {
      const result = await createLink.mutateAsync({ id: householdId });
      setUrl(result?.url ?? null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not create a link."));
    }
  };

  const handleCopy = async () => {
    if (!url) {
      return;
    }
    await navigator.clipboard?.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const handleRevoke = async () => {
    setError("");
    try {
      await revokeLink.mutateAsync({ id: householdId });
      setUrl(null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not revoke the link."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Wi-Fi sharing link</DialogTitle>
          <DialogDescription>
            Send this to the household so they can enter their own network details. Copy it now: it
            is shown once and cannot be retrieved later.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <CircleNotch className="text-muted size-5 animate-spin" weight="light" />
        ) : (
          <div className="space-y-4">
            {status && (
              <div className="bg-background rounded-lg p-3 text-[13px]">
                <p className="text-primary">{STATUS_LABEL[status.status] ?? status.status}</p>
                <p className="text-muted mt-1 text-[12px]">
                  Issued {new Date(status.created_at).toLocaleDateString()}, expires{" "}
                  {new Date(status.expires_at).toLocaleDateString()}.
                  {status.failed_attempts > 0 &&
                    ` ${status.failed_attempts} failed identity attempts.`}
                </p>
              </div>
            )}

            {url && (
              <div className="border-border flex items-center gap-2 rounded-lg border p-2">
                <input
                  readOnly
                  value={url}
                  aria-label="Sharing link"
                  className="text-primary min-w-0 flex-1 bg-transparent px-2 text-[13px] outline-none"
                />
                <Button size="sm" variant="secondary" onClick={() => void handleCopy()}>
                  {copied ? (
                    <Check className="size-4" weight="light" />
                  ) : (
                    <Copy className="size-4" weight="light" />
                  )}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            )}

            {error && <p className="text-destructive text-[13px]">{error}</p>}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => void handleCreate()}
                disabled={createLink.isPending}
              >
                <LinkIcon className="size-4" weight="light" />
                {status ? "Create a new link" : "Create sharing link"}
              </Button>
              {status?.status === "pending" && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void handleRevoke()}
                  disabled={revokeLink.isPending}
                >
                  <Trash className="size-4" weight="light" />
                  Revoke
                </Button>
              )}
            </div>

            {status?.status === "pending" && (
              <p className="text-muted text-[12px]">
                Creating a new link revokes the one already outstanding.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
